import React from "react";
import {
  Autocomplete,
  Box,
  CircularProgress,
  TextField,
  Typography,
} from "@mui/material";
import { DirectionsCar as VehicleIcon } from "@mui/icons-material";
import { useDevicesApi } from "../utlis/useDevicesApi";

type DeviceOption = {
  id: string;
  name: string;
  inventory?: boolean;
  disabled?: boolean;
};

type DeviceSearchSelectProps = {
  label?: string;
  placeholder?: string;
  multiple?: boolean;
  value: DeviceOption | DeviceOption[] | null;
  onChange: (value: DeviceOption | DeviceOption[] | null) => void;
  maxSelection?: number;
  disabled?: boolean;
  size?: "small" | "medium";
  onMaxSelectionReached?: (max: number) => void;
};

const mapOption = (device: any): DeviceOption => ({
  id: device.id?.toString(),
  name: device.name || "",
  inventory: device.inventory,
  disabled: device.disabled,
});

const DeviceSearchSelect: React.FC<DeviceSearchSelectProps> = ({
  label = "Select Device",
  placeholder,
  multiple = false,
  value,
  onChange,
  maxSelection = 10,
  disabled = false,
  size = "small",
  onMaxSelectionReached,
}) => {
  const [inputValue, setInputValue] = React.useState("");
  const { devices, loading } = useDevicesApi({
    deviceName: inputValue,
    limit: 10,
  });

  const options = React.useMemo(
    () =>
      (devices || [])
        .map(mapOption)
        .filter((device) => !device.inventory && !device.disabled),
    [devices]
  );

  const handleChange = (
    _: React.SyntheticEvent,
    newValue: DeviceOption | DeviceOption[] | null
  ) => {
    if (multiple && Array.isArray(newValue)) {
      if (newValue.length > maxSelection) {
        onMaxSelectionReached?.(maxSelection);
        return;
      }
      onChange(newValue);
      return;
    }
    onChange(newValue as DeviceOption | null);
  };

  return (
    <Autocomplete
      multiple={multiple}
      value={value as any}
      onChange={handleChange}
      options={options}
      getOptionLabel={(option) => option.name || ""}
      isOptionEqualToValue={(option, optionValue) => option.id === optionValue.id}
      filterOptions={(items) => items}
      loading={loading}
      disabled={disabled}
      inputValue={inputValue}
      onInputChange={(_, newInputValue) => setInputValue(newInputValue)}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          size={size}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
      renderOption={(props, option) => (
        <li {...props}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <VehicleIcon sx={{ color: "#6b7280", fontSize: "1rem" }} />
            <Typography sx={{ fontSize: "0.75rem" }}>{option.name}</Typography>
          </Box>
        </li>
      )}
      fullWidth
      disableClearable
    />
  );
};

export type { DeviceOption };
export default DeviceSearchSelect;
